import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';

/* ──────────────────────────────────────────────────────────────────────────
 * Loads every public CMS endpoint ONCE and shares it across the whole public
 * site so the mega-menu, listing, detail and footer never re-fetch. All data
 * comes from existing /api/website/public/* routes — nothing new on the server.
 * ────────────────────────────────────────────────────────────────────────── */

const PublicDataContext = createContext(null);

const EMPTY = {
  settings: {},
  sections: {},
  products: [],
  process: [],
  testimonials: [],
  team: [],
  menuPages: [],
};

export function PublicDataProvider({ children }) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const results = await Promise.allSettled([
          api.get('/website/public/settings'),
          api.get('/website/public/sections'),
          api.get('/website/public/products'),
          api.get('/website/public/process'),
          api.get('/website/public/testimonials'),
          api.get('/website/public/team'),
          api.get('/website/public/pages'),
        ]);
        if (!alive) return;
        const val = (i, fb) => (results[i].status === 'fulfilled' ? results[i].value.data : fb);
        setData({
          settings: val(0, {}) || {},
          sections: val(1, {}) || {},
          products: val(2, []) || [],
          process: val(3, []) || [],
          testimonials: val(4, []) || [],
          team: val(5, []) || [],
          menuPages: val(6, []) || [],
        });
      } catch (err) {
        if (alive) setError(err);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const derived = useMemo(() => {
    const products = data.products || [];

    // Categories in first-seen order (respects admin sort_order from the API).
    const seen = new Map();
    for (const p of products) {
      const key = (p.category || 'Uncategorised').trim();
      if (!seen.has(key)) seen.set(key, []);
      seen.get(key).push(p);
    }
    const categories = [...seen.entries()].map(([name, items]) => ({
      name,
      count: items.length,
      items,
      cover: items.find((i) => i.image_url)?.image_url || null,
    }));

    // Newest first for "New Arrivals" (created_at if present, else id).
    const newest = [...products].sort((a, b) => {
      const at = a.created_at ? new Date(a.created_at).getTime() : a.id || 0;
      const bt = b.created_at ? new Date(b.created_at).getTime() : b.id || 0;
      return bt - at;
    });

    return { categories, newest };
  }, [data.products]);

  const value = useMemo(
    () => ({
      ...data,
      ...derived,
      loading,
      error,
      siteName: data.settings.site_name || 'MOZI Innovators',
      getProduct: (id) =>
        (data.products || []).find((p) => String(p.id) === String(id)) || null,
      productsInCategory: (cat) =>
        (data.products || []).filter(
          (p) => (p.category || '').toLowerCase() === String(cat || '').toLowerCase()
        ),
    }),
    [data, derived, loading, error]
  );

  return <PublicDataContext.Provider value={value}>{children}</PublicDataContext.Provider>;
}

export function usePublicData() {
  const ctx = useContext(PublicDataContext);
  if (!ctx) throw new Error('usePublicData must be used within PublicDataProvider');
  return ctx;
}
